import React from "react";
import { Heading, Text, HStack, VStack, Icon, IconButton } from "@astryxdesign/core";
import { Pencil } from "lucide-react";
import styles from "./PageHeader.module.css";

export interface PageHeaderProps {
  title: string;
  description: string;
  action?: React.ReactNode;
  /**
   * When provided, a pencil button is revealed beside the title on hover
   * (and on keyboard focus) as a secondary way to edit it.
   */
  onEditTitle?: () => void;
  editTitleLabel?: string;
}

/**
 * Page header using Astryx's Heading and Text components.
 *
 * Title/description come from Astryx Heading/Text, so this MUST be rendered
 * inside an Astryx <Theme> scope.
 */
const PageHeader = ({
  title,
  description,
  action,
  onEditTitle,
  editTitleLabel = "Edit name",
}: PageHeaderProps): React.ReactElement => {
  return (
    <section className={styles.header}>
      <HStack justify="between" align="start">
        <VStack gap={1}>
          {onEditTitle ? (
            <div className={styles.titleRow}>
              <HStack gap={2} align="center">
                <Heading level={1}>{title}</Heading>
                <span className={styles.editButton}>
                  <IconButton
                    icon={<Icon icon={Pencil} />}
                    label={editTitleLabel}
                    variant="ghost"
                    size="sm"
                    onClick={onEditTitle}
                  />
                </span>
              </HStack>
            </div>
          ) : (
            <Heading level={1}>{title}</Heading>
          )}
          <Text color="secondary">{description}</Text>
        </VStack>
        {action}
      </HStack>
    </section>
  );
};

export default PageHeader;
